import { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import FamiliaFicha from '../components/FamiliaFicha';
import FichaProspecto from '../components/FichaProspecto';
import MemberFamilia from '../components/MemberFamilia';
import BecaFicha, { BecaChip } from '../components/BecaFicha';
import { CreditCard, CalendarCheck, CalendarDays, CalendarClock, User as UserIcon, Users, Mail, Cake, Ticket, BadgePercent, Phone, AlertCircle, CheckCircle2, Camera, KeyRound, FolderOpen, Receipt, Pencil, Trash2, Plus, FilePlus2, Ban, Info } from 'lucide-react';
import BackButton from '../../../components/BackButton';
import {
  getMember, updateMember, getMemberships, getDiscounts, discountLabel, age, photoUrl, Membership, Discount,
  getMemberPayments, getMemberAttendance, MemberPayment, MemberAttendance, uploadMemberPhoto,
} from '../api';
import PersonAvatar from '../../../components/PersonAvatar';
import { readCache, writeCache } from '../../../services/localCache';

/** Fecha ISO (YYYY-MM-DD) a N meses de hoy — para proponer vencimiento de un descuento temporal. */
function addMonthsISO(months: number): string {
  return addMonthsClamped(todayLocal(), months);
}
/** Etiqueta de mes/año, ej. "jun 2026". */
function monthYearEs(iso: string): string {
  return new Date(iso + 'T12:00:00').toLocaleDateString('es-MX', { month: 'short', year: 'numeric' });
}

/** Lee un File de imagen y devuelve un dataURL JPEG redimensionado (lado máx maxSide). */
function fileToResizedWebp(file: File, maxSide = 700, quality = 0.82): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('No se pudo leer el archivo'));
    reader.onload = () => {
      img.onerror = () => reject(new Error('Archivo de imagen inválido'));
      img.onload = () => {
        const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale), h = Math.round(img.height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) return reject(new Error('Canvas no disponible'));
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/webp', quality));
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  });
}
import { TYPE_LABEL, TYPE_COLOR, money, getMemberDebts, deletePayment, cancelDebt, Debt, sinPrefijoVitrina } from '../../finances/api';
import MetodoPago from '../../finances/components/MetodoPago';
import PaymentForm, { AbonoTarget } from '../../finances/components/PaymentForm';
import QuickPay from '../../finances/components/QuickPay';
import ConfirmDialog from '../../../components/ConfirmDialog';
import DebtForm from '../../finances/components/DebtForm';
import AccessModal from '../components/AccessModal';
import { getWhatsAppLink } from '../../../utils/whatsapp';
import { useAuth } from '../../../context/AuthContext';
import { useToast } from '../../../context/ToastContext';
import { todayLocal, addMonthsClamped } from '../../../utils/dates';
import EmptyState from '../../../components/EmptyState';
import MemberClasses from '../components/MemberClasses';
import MemberFiles from '../components/MemberFiles';
import AttendanceCalendar from '../components/AttendanceCalendar';
import { colorClase } from '../../../utils/classColor';
import { WEEKDAYS_SHORT } from '../../portal/api';
import PlanClasesAviso from '../components/PlanClasesAviso';
import AsistenciaResumen from '../components/AsistenciaResumen';

// El icono va POR CAMPO (Carlos 7-ago): con 7 recuadros iguales en rejilla, la etiqueta de
// texto sola no basta para distinguirlos de un vistazo — el icono se lee antes que la palabra.
const FIELDS: { key: string; label: string; type?: string; icon: typeof UserIcon }[] = [
  { key: 'firstname', label: 'Nombre', icon: UserIcon },
  { key: 'lastname', label: 'Apellidos', icon: Users },
  { key: 'email', label: 'Correo', icon: Mail },
  { key: 'phone_number', label: 'Teléfono', icon: Phone },
  { key: 'dob', label: 'Fecha de nacimiento', type: 'date', icon: Cake },
  // Identificación y Dirección FUERA (pedido de Karla 29-jul): venían del padrón de Kydemy,
  // nadie las llenó nunca (0 de 662 alumnos) y solo estiraban la ficha. Las columnas siguen
  // en BD (`id_number`, `postal_address`), así que revivirlas es volver a poner estas 2 líneas.
];

/** `{"K-pop Kids"}` → `K-pop Kids`; `{NULL}` o vacío → null (no se pinta chip). */
function limpiaAssoc(v?: string | null) {
  const t = String(v ?? '').replace(/[{}"]/g, '').trim();
  return !t || t.toUpperCase() === 'NULL' ? null : t;
}

/** Filtro del historial de «Pagos y adeudos» por concepto (Carlos 29-sep: «ver las compras de
 *  vitrina, las mensualidades, etc.»). 🪤 La vitrina tiene DOS formas: lo cobrado en la app llega
 *  como `product`, pero lo importado de Kydemy es `other` con la categoría en `assoc_name`
 *  («AyB», «A&B», «ayb adrian»…: ~2,400 pagos). Se agrupan para que el chip diga la verdad. */
const esVitrina = (p: any) =>
  p.type === 'product' || (p.type === 'other' && /^a\s*[y&]\s*b\b/i.test(limpiaAssoc(p.assoc_name) || ''));
const GRUPOS_PAGO: { key: string; label: string; match: (item: { tipo: string; row: any }) => boolean }[] = [
  { key: 'membership', label: 'Mensualidades', match: (i) => i.tipo === 'pago' && i.row.type === 'membership' },
  { key: 'license', label: 'Inscripciones', match: (i) => i.tipo === 'pago' && i.row.type === 'license' },
  { key: 'vitrina', label: 'Vitrina', match: (i) => i.tipo === 'pago' && esVitrina(i.row) },
  { key: 'clases', label: 'Clases y bonos', match: (i) => i.tipo === 'pago' && ['class', 'voucher', 'voucher_trial', 'voucher_voucher'].includes(i.row.type) },
  { key: 'event', label: 'Eventos', match: (i) => i.tipo === 'pago' && i.row.type === 'event' },
  { key: 'otros', label: 'Otros', match: (i) => i.tipo === 'pago' && !['membership', 'license', 'class', 'voucher', 'voucher_trial', 'voucher_voucher', 'event'].includes(i.row.type) && !esVitrina(i.row) },
  { key: 'cancel', label: 'Cancelados', match: (i) => i.tipo === 'cancel' },
];

const inputCls =
  'w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/30';

// Tarjeta de cada bloque del perfil (datos personales · contacto · emergencia · comentarios).
// «Plan y cobro» usa las mismas medidas pero con el tinte de marca, para que el dinero destaque.
// Fondo `canvas` (gris de página) DENTRO de la tarjeta blanca: en tema claro `surface-2` es
// casi blanco y los bloques no se distinguían («se mira muy claro» — Karla, 15-ago). En oscuro
// canvas es más oscuro que la tarjeta, así que el efecto de hueco funciona en los dos temas.
const secCls = 'rounded-xl border border-line bg-canvas px-4 pb-3.5 pt-3 shadow-inner';
// Cabecera de sección con presencia: negritas, color de texto normal (no gris), icono de marca
// y una línea que la separa de los campos (Carlos 15-ago: «el título no se nota tanto»).
const h3Cls = 'mb-2 flex items-center gap-1.5 border-b border-line pb-1.5 text-xs font-bold uppercase tracking-wider text-content';

// Acepta los dos juegos de nombres: el importado de Kydemy (attended/made_up) y el del pase de lista nuevo (present/makeup).
const ATT_LABEL: Record<string, { txt: string; cls: string }> = {
  present: { txt: 'Asistió', cls: 'text-emerald-600 dark:text-emerald-400' },
  attended: { txt: 'Asistió', cls: 'text-emerald-600 dark:text-emerald-400' },
  absent: { txt: 'Faltó', cls: 'text-red-600 dark:text-red-400' },
  makeup: { txt: 'Reposición', cls: 'text-amber-600 dark:text-amber-400' },
  made_up: { txt: 'Reposición', cls: 'text-amber-600 dark:text-amber-400' },
};

function fmtDay(d: string | null) {
  if (!d) return '—';
  const dd = new Date(String(d).slice(0, 10) + 'T12:00:00');
  return isNaN(dd.getTime()) ? '—' : dd.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
}

type Tab = 'perfil' | 'clases' | 'pagos' | 'asistencia' | 'archivos' | 'familia';

export default function MemberDetailPage() {
  const { id } = useParams();
  const { user, hasModule } = useAuth();
  const toast = useToast();
  const isAdmin = user?.role === 'admin';
  // Marketing (29-sep-2026): ve la ficha pero nada de dinero → sin pestaña Pagos ni chips de adeudo.
  const veDinero = hasModule('letsmove_finances') || hasModule('cashbox');   // cajeras sí cobran

  const [m, setM] = useState<any>(null);
  const [memberships, setMemberships] = useState<Membership[]>(() => readCache<Membership[]>('memberships-cat') ?? []);
  const [discounts, setDiscounts] = useState<Discount[]>(() => readCache<Discount[]>('discounts-cat') ?? []);
  const [, setEdit] = useState(false);   // sólo se usa para cerrar el modo edición heredado
  /** Campo que acaba de guardarse: pinta un check verde 1.8 s. Sin esto, escribir y salir del
   *  recuadro no da ninguna señal de que se guardó — y el perfil ya no tiene botón "Guardar". */
  const [campoGuardado, setCampoGuardado] = useState<string | null>(null);
  const [confirmBaja, setConfirmBaja] = useState(false);
  const [cancelando, setCancelando] = useState<number | null>(null);   // adeudo pidiendo motivo
  const [debtsCancel, setDebtsCancel] = useState<any[]>([]);           // adeudos cancelados (historial)
  const [motivoCancel, setMotivoCancel] = useState('');
  // `formState` es el estado real; `form` es el alias que usa todo el render. save() se queda
  // con `formState` para poder mezclarle un override recién elegido (ver save()).
  const [formState, setForm] = useState<Record<string, any>>({});
  const form = formState;
  const [saving, setSaving] = useState(false);
  // Guardado campo a campo EN FILA (Mabel 1-oct, video: «capturé el teléfono y no se grabó»).
  // Antes los recuadros se bloqueaban mientras viajaba cada guardado y lo que se tecleaba en ese
  // instante se perdía. Ahora los recuadros de texto siguen editables y los guardados se encadenan:
  // cada uno lee el formulario MÁS RECIENTE (formRef) al ejecutarse, así que ninguno pisa al siguiente.
  const formRef = useRef<Record<string, any>>(formState);
  formRef.current = formState;
  const filaGuardado = useRef<Promise<void>>(Promise.resolve());
  // La pestaña vive en la URL (?t=clases) para que «Volver» desde una clase regrese a la pestaña
  // en la que estabas y no al perfil (Carlos 15-ago). El historial de la SPA guarda la query, así
  // que basta con que la pestaña se refleje ahí. `replace` para no llenar el botón atrás del navegador.
  const [tabParams, setTabParams] = useSearchParams();
  const TABS_OK: Tab[] = ['perfil', 'clases', 'pagos', 'asistencia', 'archivos', 'familia'];
  const tabURL = tabParams.get('t') as Tab | null;
  const tab: Tab = tabURL && TABS_OK.includes(tabURL) && (tabURL !== 'pagos' || veDinero) ? tabURL : 'perfil';
  const setTab = (t: Tab) => {
    const p = new URLSearchParams(window.location.search);
    if (t === 'perfil') p.delete('t'); else p.set('t', t);
    setTabParams(p, { replace: true });
  };
  /** Cambiar de pestaña estando abajo del todo tiraba la vista al principio de la página: la
   *  pestaña nueva es más corta y el navegador recorta el scroll (Carlos 15-ago: «me manda hasta
   *  arriba, mantenlo a la misma altura»). Ahora, si la barra de pestañas quedó fuera de vista,
   *  se recoloca justo arriba y el contenido nuevo empieza donde estabas mirando. */
  const tabsRef = useRef<HTMLDivElement>(null);
  const irATab = (t: Tab) => {
    const cont = tabsRef.current?.closest('main');
    const arriba = cont && tabsRef.current
      ? tabsRef.current.getBoundingClientRect().top - cont.getBoundingClientRect().top
      : 0;
    setTab(t);
    if (cont && arriba < 0) {
      const y = cont.scrollTop + arriba - 8;   // 8px de aire sobre la barra
      requestAnimationFrame(() => { cont.scrollTop = y; });
    }
  };
  const [photoVer, setPhotoVer] = useState(0);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  async function onPickPhoto(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!/^image\//.test(file.type)) { toast.error('Selecciona una imagen'); return; }
    setUploadingPhoto(true);
    try {
      const dataUrl = await fileToResizedWebp(file);
      await uploadMemberPhoto(Number(id), dataUrl);
      setPhotoVer(Date.now());
      load();
      toast.success('Foto actualizada');
    } catch (err: any) { toast.error(err?.message || 'No se pudo subir la foto'); }
    finally { setUploadingPhoto(false); }
  }

  // Datos perezosos por pestaña
  const [pays, setPays] = useState<{ rows: MemberPayment[]; total: number } | null>(null);
  const [filtroPago, setFiltroPago] = useState('todos');
  // Vigencia del descuento: «perm» (sin fecha) o «fecha». Se re-sincroniza al cargar la ficha.
  const [vigenciaModo, setVigenciaModo] = useState<'perm' | 'fecha'>('perm');
  const [att, setAtt] = useState<MemberAttendance[] | null>(null);
  // Vista de asistencia: calendario mensual estilo Kydemy (default) o lista cronológica.
  const [attView, setAttView] = useState<'calendario' | 'lista'>('calendario');
  const [loadingTab, setLoadingTab] = useState(false);
  const [debtOpen, setDebtOpen] = useState(0);
  const [debts, setDebts] = useState<Debt[]>([]);
  /** Cargos ya generados que todavía NO vencen (mensualidad del mes que entra). */
  const [debtsNext, setDebtsNext] = useState<Debt[]>([]);
  const [debtNext, setDebtNext] = useState(0);
  const [abono, setAbono] = useState<AbonoTarget | null>(null);
  const [payOpen, setPayOpen] = useState(false);
  // Alta de deuda y corrección de un pago desde la ficha (Karla 29-jul, video 15:01).
  const [debtFormOpen, setDebtFormOpen] = useState(false);
  const [payEdit, setPayEdit] = useState<MemberPayment | null>(null);
  const [accessOpen, setAccessOpen] = useState(false);
  // Prospecto (F4): mini-ficha por defecto; «Ver ficha completa» abre las pestañas de siempre.
  const [verCompleta, setVerCompleta] = useState(false);

  /** Mes 'YYYY-MM' del SERVIDOR, no del navegador: se despeja de cualquier adeudo con fecha
   *  (hoy = due_date − days_to_due, y `days_to_due` sale de un DATEDIFF con CURDATE()). Importa
   *  el día 1 del mes, que es justo cuando cambia lo que se pinta en rojo: un navegador en otro
   *  huso lo adelantaría o atrasaría un día. Sin ningún adeudo con fecha, cae al reloj local. */
  function mesDelServidor(rows: Debt[]): string {
    const ref = rows.find((d) => d.due_date && d.days_to_due != null);
    const base = new Date(ref ? `${ref.due_date}T12:00:00` : new Date().toISOString().slice(0, 10) + 'T12:00:00');
    if (ref) base.setDate(base.getDate() - Number(ref.days_to_due));
    return `${base.getFullYear()}-${String(base.getMonth() + 1).padStart(2, '0')}`;
  }

  function loadDebt() {
    // Pedido de Karla (29-jul): ver y RESOLVER las deudas desde la ficha — antes solo salía el
    // chip "Debe $X" y había que irse a Finanzas para cobrarla o cancelarla.
    getMemberDebts(Number(id)).then((r) => {
      const abiertas = r.data.filter((d) => !d.settled);
      // 🚨 El cargo de un mes que TODAVÍA NO EMPIEZA no es una deuda (Karla 12-ago-2026): al
      // generar la mensualidad del mes siguiente, su ficha decía «Debe $1,500» en rojo por un
      // cargo de septiembre — «¿por qué me sale adeudo de septiembre, que aún no comienza?».
      // Se separan: el rojo es lo del mes en curso (o anterior); lo del mes que entra va aparte
      // como «Próximo cobro», y se cobra igual desde ahí — pagar por adelantado es normal aquí.
      //
      // El corte es el MES, no la fecha de vencimiento: Karla lo pidió así con todas sus letras
      // («que salgan en rojito hasta el 1ro de cada mes»). Con el corte en el `due_date` —día 3—
      // el 1 y el 2 del mes seguirían en gris, que es justo lo que no quiere. Mismo criterio que
      // las banderas de adeudo del backend (letsmove-members.js), para que no se contradigan.
      const curYm = mesDelServidor(abiertas);
      // 🟠 El DESFASADO va por SU fecha (2-nov-2026, #1628): faltando más de 3 días es «Próximo cobro»,
      // no lo que debe ahora — misma ventana que el avatar (/debt-flags) y la pestaña del Inicio.
      const desfFuturo = (d: Debt) => d.due_rule_applied === 'desfasado' && d.days_to_due != null && Number(d.days_to_due) > 3;
      const futuras = abiertas.filter((d) => desfFuturo(d) || (!!d.month && d.month.slice(0, 7) > curYm));
      const vencidas = abiertas.filter((d) => !futuras.includes(d));
      setDebts(vencidas);
      setDebtsNext(futuras);
      // Cancelados: se piden aparte (los saldados no vienen por defecto) y se listan al final del
      // historial, con su motivo — Carlos 15-ago: «que al cancelar aparezca guardado».
      getMemberDebts(Number(id), true)
        .then((rr) => setDebtsCancel(rr.data.filter((x: any) => x.cancelled_at)))
        .catch(() => {});
      setDebtOpen(vencidas.reduce((s, d) => s + Number(d.remaining ?? d.amount ?? 0), 0));
      setDebtNext(futuras.reduce((s, d) => s + Number(d.remaining ?? d.amount ?? 0), 0));
    }).catch(() => {});
  }

  /** Borrar un pago desde el historial. Si saldaba un adeudo, el backend lo reabre. */
  async function borrarPago(p: MemberPayment) {
    if (!confirm(`¿Eliminar el pago de ${money(Number(p.amount))} del ${fmtDay(p.pay_date || p.datetime)}?\n\nSi saldaba un adeudo, el adeudo se vuelve a abrir.`)) return;
    try {
      await deletePayment(p.payment_id);
      toast.success('Pago eliminado');
      setPays(null); loadDebt(); load();
    } catch (e: any) { toast.error(e?.message || 'No se pudo eliminar el pago'); }
  }

  function load() {
    getMember(Number(id)).then((r) => { setM(r.data); setForm(r.data); writeCache(`member:${id}`, r.data); setVigenciaModo(r.data?.discount_until ? 'fecha' : 'perm'); });
  }
  useEffect(() => {
    // SWR: pinta al instante la última ficha conocida y refresca por detrás
    // (mismo patrón lm-swr del resto del panel). Catálogos igual.
    const cached = readCache<any>(`member:${id}`);
    if (cached) { setM(cached); setForm(cached); setVigenciaModo(cached?.discount_until ? 'fecha' : 'perm'); }
    // 🚨 Al pasar de un alumno a otro hay que TIRAR lo cargado del anterior. Las pestañas Pagos y
    // Asistencia se cargan de forma perezosa con la condición `!pays` / `!att`: como esos estados
    // seguían llenos con los datos del alumno anterior, la condición era falsa y no se recargaba
    // nada — se veían los pagos de otra persona hasta recargar la página (Carlos 15-ago, caso
    // Kassandra León). Peligroso además de confuso: se podía cobrar mirando la ficha equivocada.
    setPays(null);
    setAtt(null);
    load();
    getMemberships().then((r) => { setMemberships(r.data); writeCache('memberships-cat', r.data); });
    getDiscounts().then((r) => { setDiscounts(r.data); writeCache('discounts-cat', r.data); });
    if (veDinero) loadDebt();
  }, [id]);

  // Carga perezosa al abrir Pagos/Asistencia
  useEffect(() => {
    if (tab === 'pagos' && !pays) {
      setLoadingTab(true);
      getMemberPayments(Number(id)).then((r) => setPays({ rows: r.data, total: r.total })).finally(() => setLoadingTab(false));
    }
    if (tab === 'asistencia' && !att) {
      setLoadingTab(true);
      getMemberAttendance(Number(id)).then((r) => setAtt(r.data)).finally(() => setLoadingTab(false));
    }
  }, [tab, id, pays, att]);

  if (!m) return <div className="text-content-3">Cargando…</div>;
  const mini = !!m.es_prospecto && !verCompleta;
  const vista: Tab | null = mini ? null : tab;   // mini-ficha: no se pinta ninguna pestaña

  /** `override` = cambios que aún no están en `form`. Hace falta porque React no actualiza el
   *  estado de forma síncrona: al guardar desde el propio onChange de un select o casilla,
   *  `form` todavía tendría el valor ANTERIOR y se guardaría lo que ya estaba. */
  function save(override?: Record<string, any>, opts?: { recargarForm?: boolean }) {
    const run = () => saveAhora(override, opts);
    filaGuardado.current = filaGuardado.current.then(run, run);
    return filaGuardado.current;
  }

  async function saveAhora(override?: Record<string, any>, opts?: { recargarForm?: boolean }) {
    const form = { ...formRef.current, ...(override || {}) };
    setSaving(true);
    try {
      const patch: Record<string, unknown> = {};
      for (const f of FIELDS) patch[f.key] = form[f.key] ?? null;
      // Contacto de emergencia (Mabel 1-oct): tenía recuadros y palomita de «guardado», pero NO
      // viajaba en el patch → nunca se grababa. El backend ya los aceptaba (PROFILE_FIELDS).
      patch.emergency_name = form.emergency_name ?? null;
      patch.emergency_phone = form.emergency_phone ?? null;
      // Sin el prefijo, el backend recompone contacts.phone SOLO con los 10 dígitos y se perdía el 52.
      // Se manda sólo si la ficha ya lo tiene: no inventamos prefijo (conviven MX 52 y US 1).
      if (form.phone_prefix != null && String(form.phone_prefix).trim() !== '') patch.phone_prefix = form.phone_prefix;
      // Las fechas llegan del backend como ISO ('1990-01-19T08:00:00.000Z'); si el campo no se
      // editó, se re-mandaría así y MariaDB rechaza el valor en columnas DATE (500 al guardar).
      patch.dob = form.dob ? String(form.dob).slice(0, 10) : null;
      patch.membership_id = form.membership_id || null;
      patch.discount_id = form.discount_id || null;
      patch.discount_until = form.discount_id ? (form.discount_until ? String(form.discount_until).slice(0, 10) : null) : null;
      patch.notes = form.notes ?? null;
      patch.active = form.active ? 1 : 0;
      await updateMember(Number(id), patch);
      if (opts?.recargarForm === false) {
        // 🚨 NO tocar `form` aquí. Al guardar campo a campo, la respuesta llega cuando el usuario
        // YA está tecleando en el siguiente recuadro: un setForm() con los datos del servidor le
        // borraría lo escrito. Se refresca sólo `m` (lo GUARDADO), que es lo que compara
        // valorGuardado() y lo que pintan los bloques de solo lectura.
        getMember(Number(id)).then((r) => { setM(r.data); writeCache(`member:${id}`, r.data); }).catch(() => {});
      } else {
        setEdit(false); load();
        toast.success('Cambios guardados');
      }
    } catch (e: any) { toast.error(e?.message || 'Error al guardar los cambios'); }
    finally { setSaving(false); }
  }

  const dobForInput = (form.dob ? String(form.dob).slice(0, 10) : '');

  /** Edad por CALENDARIO. No se usa `age()` de la API: devuelve texto y divide entre 365.25 días,
   *  así que cerca del cumpleaños falla por un año — justo el borde que decide si esta persona
   *  tiene «tutor responsable» o «contacto de emergencia». null = sin fecha de nacimiento. */
  const edadAlumno = (() => {
    if (!m.dob) return null;
    const n = new Date(`${String(m.dob).slice(0, 10)}T12:00:00`);
    if (isNaN(n.getTime())) return null;
    const h = new Date();
    let e = h.getFullYear() - n.getFullYear();
    const dm = h.getMonth() - n.getMonth();
    if (dm < 0 || (dm === 0 && h.getDate() < n.getDate())) e--;
    return e;
  })();
  /** Es el MISMO par de columnas; sólo cambia cómo se llama (Carlos 7-ago): mientras es menor,
   *  quien responde por él es su tutor; al cumplir 18 esa misma persona pasa a ser su contacto
   *  de emergencia. Nada que migrar: el dato ya está donde debe. */
  const esMenor = edadAlumno != null && edadAlumno < 18;
  const tutorLabel = esMenor ? 'Tutor responsable' : 'Contacto de emergencia';
  // Lista de datos que hacen falta para operar (se resume en un chip del encabezado).
  const faltantes = [
    [!form.phone_number, 'teléfono'], [!form.email, 'correo'], [!dobForInput, 'fecha de nacimiento'],
    [!form.emergency_name, 'contacto de emergencia'], [!form.emergency_phone, 'tel. de emergencia'],
  ].filter(([f]) => f).map(([, t]) => t as string);

  /** Dar de baja / reactivar. PREGUNTA sólo al dar de baja (Carlos 7-ago): reactivar no rompe
   *  nada, pero desactivar apaga al alumno en cobranza, multas, listas de clase, cumpleaños y
   *  métricas — 10 módulos del backend filtran por `contacts.is_active`. Con la edición directa
   *  el cambio se guarda al instante, así que un clic de más se llevaría al alumno por delante.
   *
   *  🚨 Y avisa lo que de verdad importa: `00-accounts-catalogs.js` PISA `is_active` con el valor
   *  de Kydemy en cada delta. Una baja hecha sólo aquí REVIVE en el próximo re-sync y nadie se
   *  entera. La baja de verdad la tiene que hacer Karla allá. (Al 7-ago no hay ninguna baja local
   *  en desacuerdo con Kydemy: el problema es hacia adelante, no una deuda que arrastremos.) */
  async function cambiarActivo(nuevo: boolean) {
    if (!nuevo) { setConfirmBaja(true); return; }   // reactivar no pregunta: no rompe nada
    setForm({ ...form, active: nuevo });
    await save({ active: nuevo });
  }
  /** Cancela el adeudo con su motivo y refresca la ficha. */
  async function cancelarAdeudo(debtId: number) {
    const motivo = motivoCancel.trim();
    if (!motivo) return;
    setSaving(true);
    try {
      await cancelDebt(debtId, motivo);
      setCancelando(null); setMotivoCancel('');
      toast.success('Adeudo cancelado');
      loadDebt(); load();
    } catch (e: any) { toast.error(e?.message || 'No se pudo cancelar el adeudo'); }
    finally { setSaving(false); }
  }

  async function darDeBaja() {
    setForm({ ...form, active: false });
    await save({ active: false });
  }

  /** Valor tal como está GUARDADO hoy, en el mismo formato que el input, para poder comparar. */
  function valorGuardado(key: string) {
    if (key === 'dob') return m.dob ? String(m.dob).slice(0, 10) : '';
    return m[key] == null ? '' : String(m[key]);
  }

  /** Guarda al salir del campo, y SÓLO si cambió (Carlos 7-ago: perfil editable directo, sin
   *  botón "Editar", como en Kydemy). Sin la comparación, pasar por los 5 campos con el tabulador
   *  dispararía 5 PATCH idénticos. Reusa save(), que ya manda el patch completo y recarga. */
  /** `marca` = qué check verde encender; por defecto el del propio campo. Los dos campos de
   *  emergencia comparten uno solo, para no encender dos palomitas en el mismo recuadro. */
  async function guardarCampo(key: string, marca?: string) {
    const actual = key === 'dob' ? dobForInput : (form[key] == null ? '' : String(form[key]));
    if (actual === valorGuardado(key)) return;
    setCampoGuardado(marca || key);
    await save(undefined, { recargarForm: false });
    // El check verde se apaga solo; no bloquea nada mientras tanto.
    const señal = marca || key;
    setTimeout(() => setCampoGuardado((k) => (k === señal ? null : k)), 1800);
  }

  // Estado de pago del mes en curso (para la píldora del encabezado)
  const pm = m.pay_month as null | { month: string; status: 'paid' | 'partial' | 'pending'; remaining: number; expected: number; last_pay: string | null; has_debt_row?: boolean; starts_later?: boolean; starts_month?: string; desfasado?: { due_date: string; upcoming: boolean } };
  // Empieza después (pagó un mes POSTERIOR y este no le toca): el chip habla del mes que pagó, no de este.
  const pmLabel = pm ? (() => { const s = new Date((pm.starts_later && pm.starts_month ? pm.starts_month : pm.month) + '-01T12:00:00').toLocaleDateString('es-MX', { month: 'long' }); return s.charAt(0).toUpperCase() + s.slice(1); })() : '';

  // Descuentos relevantes al plan elegido (los del plan + los generales) + el ya asignado.
  // Se ofrecen los VIVOS del plan (o generales); un descuento archivado solo aparece si el alumno ya
  // lo tiene asignado — así se conserva al guardar en vez de perderse, pero no se le da a nadie más.
  const descSel = discounts.find((d) => String(d.discount_id) === String(form.discount_id));
  const descIncluido = !!(descSel && Number(descSel.included_in_price));
  const relevantDiscounts = discounts.filter((d) =>
    (!d.deleted_at && (d.membership_id == null || String(d.membership_id) === String(form.membership_id)))
    || String(d.discount_id) === String(form.discount_id));
  // Al elegir un descuento, si tiene vigencia (meses) propone fecha de vencimiento.
  function pickDiscount(id: string) {
    const d = discounts.find((x) => String(x.discount_id) === String(id));
    const until = d && d.duration_months ? addMonthsISO(d.duration_months) : '';
    const cambio = { discount_id: id, discount_until: until };
    setForm({ ...form, ...cambio });
    setVigenciaModo(until ? 'fecha' : 'perm');
    save(cambio);   // mismo motivo que pickMembership: un select no tiene blur donde guardar
  }
  // Al cambiar de PLAN: el descuento estándar de ese plan se acomoda solo (vacío si el plan no tiene default).
  function pickMembership(id: string) {
    const p = memberships.find((x) => String(x.membership_id) === String(id));
    const defId = p && p.default_discount_id != null ? String(p.default_discount_id) : '';
    const d = discounts.find((x) => String(x.discount_id) === defId);
    const until = d && d.duration_months ? addMonthsISO(d.duration_months) : '';
    const cambio = { membership_id: id, discount_id: defId, discount_until: until };
    setForm({ ...form, ...cambio });
    setVigenciaModo(until ? 'fecha' : 'perm');
    // Se guarda al instante: un <select> no tiene "salir del campo" como un input, y dejarlo
    // pendiente sin botón "Guardar" es la forma segura de perder el cambio.
    save(cambio);
  }

  // Render de UN campo de FIELDS. Antes iba dentro de un .map() sobre una rejilla única; desde
  // el 15-ago-2026 el perfil se agrupa por SECCIONES (datos personales · contacto · plan y cobro ·
  // emergencia), y cada sección pinta los suyos — por eso el render vive aquí y no en el map.
  // Campos que la recepción SÍ puede conseguir y que hacen falta para operar: sin teléfono no se
  // avisa nada, sin fecha no hay cumpleaños, sin correo no hay acceso al portal. Se marcan en
  // ámbar cuando están vacíos (Carlos 15-ago: «que se distinga qué nos falta»).
  const CLAVE = ['phone_number', 'email', 'dob'];
  const vacio = (v: any) => v == null || String(v).trim() === '';

  const campoDe = (key: string) => {
    const f = FIELDS.find((x) => x.key === key);
    if (!f) return null;
    const Icono = f.icon;
    const recienGuardado = campoGuardado === f.key;
    const falta = CLAVE.includes(f.key) && vacio(f.key === 'dob' ? dobForInput : form[f.key]);
    return (
              <div>
                <label htmlFor={`f-${f.key}`} className="mb-0.5 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-content-3">
                  <Icono size={13} className="shrink-0" />
                  {f.label}
                  {falta && <span className="inline-flex items-center gap-0.5 normal-case text-amber-600 dark:text-amber-400"><AlertCircle size={12} /> falta</span>}
                  {recienGuardado && <CheckCircle2 size={13} className="text-emerald-500" aria-label="Guardado" />}
                </label>
                {isAdmin ? (
                  <input id={`f-${f.key}`} type={f.type || 'text'}
                    value={f.key === 'dob' ? dobForInput : (form[f.key] ?? '')}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                    onBlur={() => guardarCampo(f.key)}
                    // Enter guarda sin tener que salir con el ratón; Escape deshace lo tecleado.
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.currentTarget.blur(); }
                      if (e.key === 'Escape') { setForm({ ...form, [f.key]: valorGuardado(f.key) }); e.currentTarget.blur(); }
                    }}
                    className={`${inputCls} ${recienGuardado ? 'border-emerald-500/60' : falta ? 'border-amber-500/50 bg-amber-500/[0.04]' : ''}`} />
                ) : (
                  <div className="text-sm text-content">{f.key === 'dob' ? (dobForInput || '—') : (m[f.key] || '—')}</div>
                )}
              </div>
    );
  };

  const tabs: { key: Tab; label: string; icon: typeof UserIcon }[] = [
    { key: 'perfil', label: 'Perfil', icon: UserIcon },
    { key: 'clases', label: 'Clases', icon: CalendarDays },
    ...(veDinero ? [{ key: 'pagos' as Tab, label: 'Pagos', icon: CreditCard }] : []),
    { key: 'asistencia', label: 'Asistencia', icon: CalendarCheck },
    { key: 'archivos', label: 'Archivos', icon: FolderOpen },
    // 23-sep-2026 (Carlos): familia y descuento en su propia pestaña, con tarjeta por familiar.
    { key: 'familia', label: 'Familia', icon: Users },
  ];

  return (
    // Reintento 28-jul (patrón "Expediente del paciente" de Jesara, pedido Carlos): UNA sola
    // banda de cabecera compacta (foto + nombre + chips + acciones) con las pestañas pegadas
    // debajo — nada de partir en dos tarjetas: en móvil las pestañas quedan a un pulgar.
    // El contenido de cada pestaña va a lo ancho (denso 1600px; Perfil en 3 columnas en xl).
    // El aire entre la barra superior y la ficha se recorta a la mitad en escritorio (Carlos
    // 15-ago: «queda mucho espacio entre el breadcrumb y el perfil»): el «Volver» ya vive en la
    // barra, así que ese margen sólo separaba dos cosas que se leen juntas.
    <div className="md:-mt-3">
      <BackButton fallback="/alumnos" fallbackLabel="alumnos" />

      {/* ===== Cabecera compacta + pestañas ===== */}
      <div className="rounded-2xl bg-surface shadow-sm">
        <div className="flex flex-col items-center gap-4 p-5 text-center sm:flex-row sm:items-center sm:gap-6 sm:text-left">
          {/* RETRATO GRANDE (15-ago-2026, Karla vía Carlos: «como en Kydemy»). Vive en la CABECERA,
              no en la pestaña Perfil, para que sea UNA sola foto visible también en Clases, Pagos,
              Asistencia y Archivos — ahí la cabecera es lo único que dice de quién es la pantalla. */}
          <div className="relative h-32 w-32 shrink-0 sm:h-[200px] sm:w-[200px]">
            <PersonAvatar contactId={m.account_id} hasPhoto={m.has_photo}
              photoSrc={`${photoUrl(m.account_id)}${photoVer ? `?v=${photoVer}` : ''}`}
              name={m.firstname} lastName={m.lastname}
              size="h-full w-full" initialClass="text-5xl sm:text-7xl" rounded="rounded-full"
              className="lm-avatar--xl ring-1 ring-line rounded-full" badgeCorner="tr" />
            {isAdmin && (
              <>
                {/* TODO el avatar es el botón de cambiar foto: así se puede tocar aunque no haya
                    icono encima. `inset-0` queda POR DEBAJO de la insignia de adeudo (que va en
                    la esquina con z propio), de modo que no le roba el clic. */}
                <button onClick={() => fileRef.current?.click()} disabled={uploadingPhoto}
                  className="absolute inset-0 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue"
                  title={m.has_photo ? 'Cambiar foto' : 'Subir foto'}
                  aria-label={m.has_photo ? 'Cambiar foto' : 'Subir foto'} />
                {/* Con el retrato grande la cámara ya cabe SIEMPRE sin tapar la cara (antes, sobre
                    64 px, se ocultaba cuando había foto — Carlos 7-ago). Discreta si hay foto;
                    grande si falta, que es cuando hay que llamar la atención. */}
                <span
                  className={`pointer-events-none absolute grid place-items-center rounded-full bg-accent-solid text-on-accent shadow ring-4 ring-surface ${m.has_photo ? 'bottom-1 right-1 h-8 w-8 sm:h-10 sm:w-10' : 'bottom-2 right-2 h-10 w-10 sm:h-14 sm:w-14'}`}>
                  <Camera size={m.has_photo ? 16 : 22} />
                </span>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPickPhoto} />
              </>
            )}
            {uploadingPhoto && <div className="absolute inset-0 grid place-items-center rounded-full bg-black/40 text-xs font-medium text-white sm:text-sm">Subiendo…</div>}
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-baseline justify-center gap-x-3 gap-y-0.5 sm:justify-start">
              <h2 className="font-display text-2xl font-bold leading-tight text-content sm:text-3xl">{m.firstname} {m.lastname}</h2>
              <span className="text-sm text-content-2">
                {m.es_prospecto ? 'Prospecto' : m.role === 'student' ? 'Alumno' : m.role} · {age(m.dob)} años
                {!m.active && <> · <span className="text-content-3">inactivo</span></>}
              </span>
            </div>
            {/* LÍNEA 2 — plan y lo que paga al mes (Carlos 15-ago: «la mensualidad quedaba muy
                abajo»). Antes vivía en un recuadro al final de la pestaña Perfil, donde había que
                bajar a buscarlo; aquí se lee de un vistazo y en TODAS las pestañas. */}
            <div className="mt-1 flex flex-wrap items-baseline justify-center gap-x-2 gap-y-0.5 sm:justify-start">
              {m.membership_name
                ? <span className="font-medium text-accent-ink">{m.membership_name}</span>
                : <span className="text-content-3">Sin mensualidad</span>}
              {m.effective_month != null && Number(m.membership_price_month) > 0 && (
                <>
                  <span className="text-content-3">·</span>
                  {/* 29-sep: un descuento que ya va DENTRO del precio no tacha nada (tachaba $1,300 y
                      ponía $1,300 al lado) ni «vence»; solo se nombra. */}
                  {m.discount_name && !m.discount_expired && !m.discount_included && (
                    <span className="text-sm text-content-3 line-through">{money(m.membership_price_month)}</span>
                  )}
                  <span className="font-display text-lg font-bold text-content">{money(m.effective_month)}</span>
                  <span className="text-sm text-content-3">/mes</span>
                  {m.discount_name && m.discount_included ? (
                    <span className="rounded bg-surface-2 px-1.5 py-0.5 text-xs font-medium text-content-2" title="Este descuento ya está dentro del precio del plan: no se resta ni vence.">
                      incluye {m.discount_name}
                    </span>
                  ) : m.discount_name && !m.discount_expired && (
                    <span className="rounded bg-brand-blue/10 px-1.5 py-0.5 text-xs font-medium text-brand-blue">
                      {m.discount_name} {discountLabel(m.discount_value, m.discount_is_percentage)}
                    </span>
                  )}
                </>
              )}
            </div>
            {!!m.active && <PlanClasesAviso check={m.plan_check} className="mt-1.5 max-w-xl text-left" />}
            {/* Chips: estado del mes / deuda / WhatsApp / etiquetas / niveles */}
            <div className="mt-1.5 flex flex-wrap items-center justify-center gap-2 sm:justify-start">
            {/* Con el adeudo del mes ya creado, el chip «falta $X» y el chip «Debe $X» son la
                misma deuda contada dos veces: se deja solo el rojo, que es el de Kydemy. El
                «pagado» en verde sí se conserva (esa información no la da el otro chip). */}
            {/* 🟠 Desfasado que aún no vence (3-nov-2026, Karla: Nancy Santiago): «próximo cobro» neutro con SU
                monto y SU fecha, no «falta» en ámbar. A 3 días de la fecha vuelve al chip normal. */}
            {veDinero && pm && pm.desfasado?.upcoming ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-surface-2 px-2.5 py-1 text-xs font-semibold text-content-2">
                <CalendarClock size={13} /> Desfasado · cobra el {new Date(pm.desfasado.due_date + 'T12:00:00').toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })} · {money(pm.remaining)}
              </span>
            ) : veDinero && pm && (pm.status === 'paid' ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2.5 py-1 text-xs font-semibold text-emerald-600 dark:text-emerald-400">
                <CheckCircle2 size={13} /> {pmLabel} pagado
              </span>
            ) : pm.has_debt_row && debtOpen > 0 ? null : (
              <button
                onClick={() => isAdmin && setPayOpen(true)}
                disabled={!isAdmin}
                title={isAdmin ? 'Registrar pago del mes' : undefined}
                className={`inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2.5 py-1 text-xs font-semibold text-amber-600 dark:text-amber-400 ${isAdmin ? 'transition hover:bg-amber-500/25 cursor-pointer' : 'cursor-default'}`}>
                <AlertCircle size={13} /> {pmLabel} · falta {money(pm.remaining)}
              </button>
            ))}
            {veDinero && <BecaChip beca={m.beca} />}
            {veDinero && debtOpen > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-red-500/10 px-2.5 py-1 text-xs font-semibold text-red-600 dark:text-red-400">
                <AlertCircle size={13} /> Debe {money(debtOpen)}
              </span>
            )}
            {/* Resumen de huecos: se ve desde cualquier pestaña y dice CUÁLES faltan al pasar el
                ratón. El detalle campo por campo está en Perfil, en ámbar. */}
            {/* Un prospecto es solo nombre + teléfono (Karla 30-sep): no se le reclaman datos de alumno. */}
            {faltantes.length > 0 && !m.es_prospecto && (
              <span title={`Faltan: ${faltantes.join(', ')}`}
                className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2.5 py-1 text-xs font-semibold text-amber-600 dark:text-amber-400">
                <AlertCircle size={13} /> {faltantes.length === 1 ? `Falta ${faltantes[0]}` : `Faltan ${faltantes.length} datos`}
              </span>
            )}
            {m.phone_number && getWhatsAppLink(m.phone_prefix, m.phone_number, '') && (
              <a href={getWhatsAppLink(m.phone_prefix, m.phone_number, `Hola ${m.firstname || ''} 👋`)!} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1 rounded-full border border-line px-2.5 py-1 text-xs font-medium text-emerald-600 transition hover:bg-surface-2 dark:text-emerald-400">
                <Phone size={13} /> WhatsApp
              </a>
            )}
            {(m.tags || []).map((t: any) => (
              <span key={t.tag_id} className="inline-block rounded-full px-2.5 py-1 text-xs font-medium text-white" style={{ background: t.color || '#64748b' }}>{t.name}</span>
            ))}
            {/* Los chips de disciplina/nivel («EF · NU», «SAL · N0») se quitaron el 15-ago:
                son códigos de Kydemy que nadie lee y ensuciaban el renglón de WhatsApp. El dato
                sigue en la BD (letsmove_member_levels) por si algún día se usa con nombres. */}
            </div>
            {/* Última asistencia y faltas seguidas (#1418, Mabel 16-oct): en la cabecera para que
                recepción vea una baja de hecho sin abrir la pestaña Asistencia. */}
            {!m.es_prospecto && <AsistenciaResumen memberId={Number(id)} onVer={() => irATab('asistencia')} />}
          </div>

          {/* CABECERA SIN BOTONES (Carlos 7-ago): "Registrar pago", "Generar acceso" y "Editar"
              hacían ruido sobre el nombre del alumno. Cada uno bajó a donde se usa:
                · Registrar pago → pestaña Pagos (ya estaba ahí, salía duplicado)
                · Generar acceso → final de la pestaña Perfil
                · Editar        → desapareció: los datos se editan directo en su recuadro */}
        </div>

        {/* Pestañas pegadas a la cabecera (patrón Jesara) */}
        {!mini && <div ref={tabsRef} className={`grid ${veDinero ? 'grid-cols-6' : 'grid-cols-5'} gap-1 border-t border-line px-2 sm:flex sm:px-3`}>
          {tabs.map(({ key, label, icon: Icon }) => (
            <button key={key} onClick={() => irATab(key)}
              className={`flex flex-col items-center justify-center gap-1 border-b-2 px-1 py-2 text-[11px] font-medium leading-tight transition sm:flex-row sm:gap-2 sm:px-4 sm:py-2.5 sm:text-sm ${
                tab === key ? 'border-accent text-accent-ink' : 'border-transparent text-content-2 hover:text-content'
              }`}>
              <Icon size={16} className="shrink-0" /> <span>{label}</span>
            </button>
          ))}
        </div>}
      </div>

      {(m.es_prospecto || m.prospecto_conflicto) ? (
        <FichaProspecto m={m} puedeConvertir={isAdmin} verCompleta={verCompleta}
          onVerCompleta={() => setVerCompleta(true)}
          onConvertido={() => { load(); setTab('clases'); }} />
      ) : null}

      {/* ===== PERFIL (datos a lo ancho, 3 columnas en xl) ===== */}
      {vista === 'perfil' && (
        <div className="mt-3 rounded-2xl bg-surface p-4 shadow-sm">
          {/* ===== PERFIL POR SECCIONES (15-ago-2026, pedido de Carlos) =====
              Antes era UNA rejilla de 9 campos: al fluir de 3 en 3, «Membresía» caía al final de
              una fila y «Descuento» al principio de la siguiente, que son justo los dos que se
              leen juntos. Agrupar por tema los mantiene pegados pase lo que pase con el ancho de
              la pantalla, y de paso nombre+apellidos y los dos de emergencia quedan emparejados.
              Los campos conservan el ancho de 1/3 (no se estiran) aunque la sección tenga 2. */}
          {/* Rejilla de secciones: en pantalla ancha caben DOS por fila (Contacto|Plan,
              Emergencia|Comentarios). Antes cada sección ocupaba un renglón entero con
              dos campos y un tercio vacío: la ficha se estiraba de más (Carlos 15-ago). */}
          <div className="grid grid-cols-1 items-start gap-3 xl:grid-cols-2">
          {/* Cada sección va en su PROPIA tarjeta (Carlos 15-ago: «se ve todo junto, no se nota la
              separación»): un título en gris no bastaba para partir la rejilla, así que ahora cada
              grupo tiene borde y fondo propios. «Plan y cobro» conserva el tinte de marca. */}
          <section className={`${secCls} xl:col-span-2`}>
            <h3 className={h3Cls}><UserIcon size={13} className="text-accent-ink" /> Datos personales</h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {campoDe('firstname')}
              {campoDe('lastname')}
              {campoDe('dob')}
            </div>
          </section>
          <section className={secCls}>
            <h3 className={h3Cls}><Phone size={13} className="text-accent-ink" /> Contacto</h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {campoDe('phone_number')}
              {campoDe('email')}
            </div>
          </section>
          {isAdmin && (
          <section className={secCls}>
            <h3 className={h3Cls}><AlertCircle size={13} className="text-accent-ink" /> En caso de emergencia</h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {/* ===== TUTOR / CONTACTO DE EMERGENCIA =====
                Dos campos más de la rejilla (Carlos 7-ago), no un bloque aparte: son datos del
                alumno como el correo, y en su propio recuadro a lo ancho pesaban demasiado.
                A quién llamar si le pasa algo. El nombre de la etiqueta cambia con la edad; la
                columna es la misma, así que al cumplir 18 no hay nada que mover.
                El ámbar sólo aprieta en MENORES: en un adulto es deseable, en un niño es serio. */}
                <div>
                  <label htmlFor="f-emg-name" className="mb-0.5 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-content-3">
                    <UserIcon size={13} className="shrink-0" /> {tutorLabel}
                    {vacio(form.emergency_name) && <span className="inline-flex items-center gap-0.5 normal-case text-amber-600 dark:text-amber-400"><AlertCircle size={12} /> falta</span>}
                    {campoGuardado === 'emergency' && <CheckCircle2 size={13} className="text-emerald-500" aria-label="Guardado" />}
                  </label>
                  {/* Sin placeholder (Carlos 15-ago): el gris «A quién llamar» y un teléfono de
                      ejemplo se leían como dato capturado. Vacío = vacío, igual que en correo. */}
                  <input id="f-emg-name" type="text"
                    value={form.emergency_name ?? ''}
                    onChange={(e) => setForm({ ...form, emergency_name: e.target.value })}
                    onBlur={() => guardarCampo('emergency_name', 'emergency')}
                    className={`${inputCls} ${vacio(form.emergency_name) ? 'border-amber-500/50 bg-amber-500/[0.04]' : ''}`} />
                </div>
                <div>
                  <label htmlFor="f-emg-phone" className="mb-0.5 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-content-3">
                    <Phone size={13} className="shrink-0" /> Tel. {esMenor ? 'del tutor' : 'de emergencia'}
                    {vacio(form.emergency_phone) && <span className="inline-flex items-center gap-0.5 normal-case text-amber-600 dark:text-amber-400"><AlertCircle size={12} /> falta</span>}
                  </label>
                  <input id="f-emg-phone" type="tel"
                    value={form.emergency_phone ?? ''}
                    onChange={(e) => setForm({ ...form, emergency_phone: e.target.value })}
                    onBlur={() => guardarCampo('emergency_phone', 'emergency')}
                    className={`${inputCls} ${vacio(form.emergency_phone) ? 'border-amber-500/50 bg-amber-500/[0.04]' : ''}`} />
                </div>
            </div>
          </section>
          )}
          {/* Fondo propio: Carlos pidió distinguir la sección del dinero del resto. */}
          <section className="rounded-xl border border-accent/30 bg-accent/[0.07] px-4 pb-3.5 pt-3">
            <h3 className={h3Cls}><Ticket size={13} className="text-accent-ink" /> Plan y cobro</h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-0.5 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-content-3">
                <Ticket size={13} className="shrink-0" /> Mensualidad
              </label>
              {isAdmin ? (
                <select value={form.membership_id || ''} disabled={saving}
                  onChange={(e) => pickMembership(e.target.value)} className={inputCls}>
                  <option value="">— Sin mensualidad —</option>
                  {memberships.map((mm) => <option key={mm.membership_id} value={mm.membership_id}>{mm.name}</option>)}
                </select>
              ) : (
                <div className="text-sm text-content">{m.membership_name || '—'}</div>
              )}
            </div>
            <div>
              <label className="mb-0.5 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-content-3">
                <BadgePercent size={13} className="shrink-0" /> Descuento
              </label>
              {isAdmin ? (
                <>
                  <select value={form.discount_id || ''} disabled={saving} onChange={(e) => pickDiscount(e.target.value)} className={inputCls}>
                    <option value="">— Sin descuento —</option>
                    {relevantDiscounts.map((dd) => (
                      <option key={dd.discount_id} value={dd.discount_id}>{dd.name} ({discountLabel(dd.value, dd.is_percentage)}){dd.duration_months ? ` · ${dd.duration_months} meses` : ''}{dd.deleted_at ? ' · archivado' : ''}</option>
                    ))}
                  </select>
                  {/* VIGENCIA (Carlos 29-sep: «no sabíamos de qué trataba… sentíamos que había que poner una
                      fecha»). Un descuento que ya va en el precio no tiene vigencia. Los reales arrancan en
                      «Permanente»; «Hasta una fecha» abre el calendario, con la explicación a la vista. */}
                  {form.discount_id && descIncluido && (
                    <p className="mt-1.5 text-xs text-content-3" data-testid="dto-incluido">
                      Ya va dentro del precio del plan: no se resta ni vence.
                    </p>
                  )}
                  {form.discount_id && !descIncluido && (
                    <div className="mt-2" data-testid="dto-vigencia">
                      <label className="mb-1 flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-content-3">
                        Vigencia del descuento
                        <span title="Hasta cuándo aplica el descuento. Pasada esa fecha se cobra el precio completo del plan." className="cursor-help normal-case text-content-3"><Info size={12} /></span>
                      </label>
                      <div className="flex flex-wrap items-center gap-2">
                        <div className="inline-flex rounded-lg border border-line bg-surface p-0.5" role="group" aria-label="Vigencia del descuento">
                          {[{ k: 'perm', t: 'Permanente' }, { k: 'fecha', t: 'Hasta una fecha' }].map((o) => {
                            const on = (vigenciaModo === o.k);
                            return (
                              <button key={o.k} type="button" disabled={saving} aria-pressed={on}
                                onClick={() => {
                                  if (o.k === 'perm') { setVigenciaModo('perm'); if (form.discount_until) { setForm({ ...form, discount_until: '' }); save({ discount_until: null }); } }
                                  else setVigenciaModo('fecha');
                                }}
                                className={`rounded-md px-2.5 py-1 text-xs font-semibold transition ${on ? 'bg-accent-solid text-on-accent' : 'text-content-2 hover:bg-surface-2'}`}>
                                {o.t}
                              </button>
                            );
                          })}
                        </div>
                        {vigenciaModo === 'fecha' && (
                          <input type="date" value={form.discount_until ? String(form.discount_until).slice(0, 10) : ''} disabled={saving} autoFocus={!form.discount_until}
                            onChange={(e) => setForm({ ...form, discount_until: e.target.value })}
                            onBlur={() => guardarCampo('discount_until')} className={`${inputCls} w-auto`} />
                        )}
                      </div>
                      <p className="mt-1 text-[11px] text-content-3">
                        {vigenciaModo === 'perm'
                          ? 'Se aplica cada mes mientras tenga el descuento.'
                          : 'Hasta ese día se aplica; después se cobra el precio completo del plan.'}
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-sm text-content">
                  {m.discount_name ? (
                    <>
                      <span className={m.discount_expired && !m.discount_included ? 'line-through text-content-3' : ''}>{m.discount_name} </span>
                      <span className="ml-1 rounded bg-brand-blue/10 px-1.5 py-0.5 text-xs font-medium text-brand-blue">{discountLabel(m.discount_value, m.discount_is_percentage)}</span>
                      {m.discount_included
                        ? <span className="ml-2 text-xs text-content-3">incluido en el precio</span>
                        : m.discount_expired
                        ? <span className="ml-2 rounded bg-red-500/15 px-1.5 py-0.5 text-[11px] font-medium text-red-600 dark:text-red-400">vencido</span>
                        : m.discount_until
                          ? <span className="ml-2 text-xs text-content-3">vence {monthYearEs(String(m.discount_until).slice(0, 10))}</span>
                          : <span className="ml-2 text-xs text-content-3">permanente</span>}
                    </>
                  ) : <span className="text-content-3">Sin descuento</span>}
                </div>
              )}
              {/* Familia (23-sep-2026): confirmada + posible; la confirmada alimenta la revisión mensual de descuentos. */}
              <FamiliaFicha familia={Array.isArray(m.familia) ? m.familia : []}
                posible={Array.isArray(m.posible_familia) ? m.posible_familia : []} puedeEditar={isAdmin} onGestionar={() => irATab('familia')} />
            </div>
            </div>
          </section>
          <section className={secCls}>
            {/* Comentarios del alumno (accounts.notes) — a todo lo ancho de la rejilla */}
            <h3 className={h3Cls}>
              <Pencil size={13} className="text-accent-ink" /> Comentarios
              {campoGuardado === 'notes' && <CheckCircle2 size={13} className="text-emerald-500" aria-label="Guardado" />}
            </h3>
            <div>
              {isAdmin ? (
                <textarea id="f-notes" value={form.notes ?? ''} rows={4}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  onBlur={() => guardarCampo('notes')}
                  className={`${inputCls} ${campoGuardado === 'notes' ? 'border-emerald-500/60' : ''}`} />
              ) : (
                <div className="whitespace-pre-wrap text-sm text-content-2">{m.notes || '—'}</div>
              )}
            </div>
          </section>
          </div>


          {/* Sin "Guardar" ni "Cancelar": cada campo se guarda al salir de él. Escape deshace
              lo tecleado en el campo en curso. */}

          {/* ===== ACCIONES DE LA FICHA (no son datos del alumno) =====
              Separadas de la rejilla a propósito (Carlos 7-ago): "Activo" parecía un campo más
              entre el correo y el teléfono, y no lo es — es un interruptor que apaga al alumno
              en cobranza, multas, listas, cumpleaños y métricas. */}
          {isAdmin && (
            <div className="mt-5 space-y-3 border-t border-line pt-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-sm font-medium text-content">
                    {form.active
                      ? <><CheckCircle2 size={15} className="shrink-0 text-emerald-500" /> Alumno activo</>
                      : <><AlertCircle size={15} className="shrink-0 text-content-3" /> Dado de baja</>}
                  </div>
                  <div className="mt-0.5 text-xs text-content-3">
                    {form.active
                      ? 'Cuenta en listas de clase, cumpleaños, cobranza y reportes.'
                      : 'Fuera de listas de clase y cumpleaños. Sus adeudos tampoco se cobran. El historial se conserva.'}
                  </div>
                </div>
                <button onClick={() => cambiarActivo(!form.active)} disabled={saving}
                  className={`shrink-0 rounded-lg border px-3 py-1.5 text-sm font-medium transition disabled:opacity-50 ${
                    form.active
                      ? 'border-line bg-surface text-content-2 hover:border-red-500/50 hover:text-red-600 dark:hover:text-red-400'
                      : 'border-emerald-500/40 bg-surface text-emerald-600 hover:bg-emerald-500/10 dark:text-emerald-400'}`}>
                  {form.active ? 'Dar de baja' : 'Reactivar'}
                </button>
              </div>

              {/* GENERAR ACCESO bajó aquí (Carlos 7-ago): arriba, junto al nombre, hacía ruido —
                  es algo que se hace una vez en la vida del alumno, no en cada visita. */}
              {(m.role === 'student' || m.role === 'teacher') && (
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="text-xs text-content-3">
                    Acceso al portal para que {m.firstname} vea sus clases y pagos.
                  </div>
                  <button onClick={() => setAccessOpen(true)}
                    className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-lg border border-line bg-surface px-3 py-1.5 text-sm font-medium text-content-2 transition hover:bg-surface-2">
                    <KeyRound size={15} /> Generar acceso
                  </button>
                </div>
              )}
            </div>
          )}

          {confirmBaja && (
            <ConfirmDialog
              title={`Dar de baja a ${m.firstname}`}
              lines={[
                `${m.firstname} ${m.lastname} sale de las listas de clase y del muro de cumpleaños.`,
                'Sus adeudos siguen registrados, pero HOY dejan de sumar en «Por cobrar» y el cobro '
                + 'automático de pagos tardíos ya no los toma. Si te debe dinero, cóbralo antes de darla de baja.',
                'Nada se borra: pagos, asistencias y adeudos quedan en su ficha, y reactivarla lo devuelve todo.',
              ]}
              warning={'La baja definitiva se hace en Kydemy. Si allá sigue activa, el próximo re-sync '
                + 'la reactivará aquí sin avisar.'}
              confirmLabel="Sí, dar de baja"
              cancelLabel="No, dejarla activa"
              onConfirm={darDeBaja}
              onClose={() => setConfirmBaja(false)}
            />
          )}
        </div>
      )}

      {/* ===== CLASES ===== */}
      {vista === 'clases' && (
        <div className="mt-5 rounded-2xl bg-surface p-6 shadow-sm">
          {/* El título y el botón «Añadir clase» los pinta MemberClasses en la MISMA fila: con el
              h3 aquí, el botón quedaba a media pantalla (Carlos 15-ago). */}
          <MemberClasses memberId={Number(id)} isAdmin={isAdmin} />
        </div>
      )}

      {/* ===== FAMILIA (descuento + familia registrada/posible + registrar) ===== */}
      {vista === 'familia' && (
        <div className="mt-3 rounded-2xl bg-surface p-4 shadow-sm">
          <MemberFamilia contactId={Number(id)} nombre={`${m.firstname || ''}`} m={m}
            familia={Array.isArray(m.familia) ? m.familia : []} posible={Array.isArray(m.posible_familia) ? m.posible_familia : []}
            discounts={relevantDiscounts} discountSel={String(form.discount_id ?? '')} onPickDiscount={pickDiscount}
            puedeEditar={isAdmin} onCambio={load} />
        </div>
      )}

      {(vista === 'pagos' || vista === 'asistencia' || vista === 'archivos') && (
      <div className="mt-5 rounded-2xl bg-surface p-6 shadow-sm">

        {/* ===== PAGOS ===== */}
        {tab === 'pagos' && (
          <div>
            {/* Cabecera con las 2 altas, como en Kydemy: REGISTRAR PAGO / REGISTRAR DEUDA. */}
            {isAdmin && (
              <div className="mb-4 flex flex-wrap items-center justify-between gap-2 border-b border-line pb-1.5">
                <h3 className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-content">
                  <CreditCard size={13} className="text-accent-ink" /> Pagos y adeudos
                </h3>
                <div className="flex gap-2">
                  <button onClick={() => setPayOpen(true)}
                    className="flex items-center gap-1.5 rounded-lg bg-accent-solid px-3 py-1.5 text-xs font-semibold text-on-accent transition hover:bg-accent-solid/90">
                    <Plus size={14} /> Registrar pago
                  </button>
                  <button onClick={() => setDebtFormOpen(true)}
                    className="flex items-center gap-1.5 rounded-lg border border-red-500/40 px-3 py-1.5 text-xs font-semibold text-red-600 transition hover:bg-red-500/10 dark:text-red-400">
                    <FilePlus2 size={14} /> Registrar deuda
                  </button>
                </div>
              </div>
            )}
            {/* Beca (7-oct-2026, Mabel): la registra cualquiera que cobre; el backend decide qué cubre. */}
            {veDinero && (
              <BecaFicha memberId={Number(id)} nombre={`${m.firstname || ''} ${m.lastname || ''}`.trim()}
                beca={m.beca || null} becas={Array.isArray(m.becas) ? m.becas : []} finCiclo={m.fin_ciclo || null}
                puedeEditar={veDinero} onCambio={() => { loadDebt(); load(); }} />
            )}
            {/* Deudas pendientes ARRIBA, como en Kydemy: cada una se cobra o se cancela aquí mismo.
                La misma caja se pinta DOS veces: en rojo lo vencido y en neutro lo que aún no vence
                («Próximo cobro»). Se cobra igual en las dos — pagar por adelantado es normal aquí. */}
            {[
              { rows: debts, total: debtOpen, futura: false },
              { rows: debtsNext, total: debtNext, futura: true },
            ].filter((caja) => caja.rows.length > 0).map((caja) => (
              <div key={caja.futura ? 'next' : 'due'}
                className={`mb-5 overflow-hidden rounded-xl border ${caja.futura ? 'border-line bg-canvas' : 'border-red-500/25 bg-red-500/[0.04]'}`}>
                <div className={`flex items-center justify-between gap-2 border-b px-4 py-2 ${caja.futura ? 'border-line' : 'border-red-500/20'}`}>
                  <h4 className="flex items-center gap-1.5 text-sm font-semibold text-content">
                    {caja.futura
                      ? <><CalendarClock size={15} className="text-content-3" /> Próximo cobro</>
                      : <><AlertCircle size={15} className="text-red-500" /> Adeudos pendientes</>}
                  </h4>
                  {/* "Total" explícito (Carlos 7-ago): con UN solo adeudo, esta cifra y la del
                      renglón son idénticas y parecían repetidas. La palabra las distingue. */}
                  <span className="flex items-baseline gap-1.5">
                    <span className="text-[11px] font-medium uppercase tracking-wide text-content-3">Total</span>
                    <span className={`font-display text-sm font-bold ${caja.futura ? 'text-content-2' : 'text-red-600 dark:text-red-400'}`}>{money(caja.total)}</span>
                  </span>
                </div>
                <ul className="divide-y divide-line/60">
                  {caja.rows.map((d) => {
                    const resta = Number(d.remaining ?? d.amount);
                    const abonado = Number(d.paid_amount || 0);
                    // El chip de tipo ya dice «Vitrina»: al comentario del fiado se le quita ese prefijo (Carlos 29-sep).
                    const etiqueta = (d.debt_type === 'product' ? sinPrefijoVitrina(d.comment) : d.comment) || d.membership_name || (d.month ? monthYearEs(d.month.slice(0, 7) + '-01') : 'Adeudo');
                    return (
                      <li key={d.debt_id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-1.5 text-sm font-medium text-content">
                            {/* El concepto lleva el mismo color que en el historial de pagos */}
                            {d.debt_type && (
                              <span className={`rounded px-1.5 py-0.5 text-xs font-semibold ${TYPE_COLOR[d.debt_type] || TYPE_COLOR.other}`}>
                                {TYPE_LABEL[d.debt_type] || d.debt_type}
                              </span>
                            )}
                            <span className="truncate">{etiqueta}</span>
                            {d.month && <span className="rounded bg-accent/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-accent-ink">{monthYearEs(d.month.slice(0, 7) + '-01')}</span>}
                          </div>
                          <div className="text-xs text-content-3">
                            {d.due_date
                              ? (caja.futura ? `se cobra el ${fmtDay(d.due_date)}` : `vence ${fmtDay(d.due_date)}`)
                              : 'sin vencimiento'}
                            {/* Cuánto lleva vencido, en días: la fecha sola obliga a hacer la cuenta
                                mental (Carlos 15-ago). `days_to_due` = due_date − hoy, así que
                                negativo = vencido. */}
                            {d.days_to_due != null && (
                              Number(d.days_to_due) < 0 ? (
                                <span className="ml-1 font-semibold text-red-600 dark:text-red-400">
                                  · vencido hace {Math.abs(Number(d.days_to_due))} día{Math.abs(Number(d.days_to_due)) === 1 ? '' : 's'}
                                </span>
                              ) : Number(d.days_to_due) === 0 ? (
                                <span className="ml-1 font-semibold text-amber-600 dark:text-amber-400">· vence hoy</span>
                              ) : (
                                <span className="ml-1">· en {d.days_to_due} día{Number(d.days_to_due) === 1 ? '' : 's'}</span>
                              )
                            )}
                            {abonado > 0 && ` · abonado ${money(abonado)}`}
                            {/* 'clone' ya no es solo el asistente de ciclo: el generador mensual
                                (scripts/generar-mensualidades.mjs) usa el mismo source. */}
                            {d.source === 'clone' && ' · generado por el sistema'}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-display text-sm font-bold text-content">{money(resta)}</span>
                          {isAdmin && (
                            <>
                              {/* COBRO EXPRESS (Carlos 7-ago): antes este era un botón "Registrar
                                  pago de $X" que abría el modal de ~10 campos. En Kydemy el mismo
                                  paso es elegir cómo paga y ya. El modal sigue a un clic, en "Otro…". */}
                              {/* Sin "Otro…" ni "Cancelar" (Carlos 7-ago): el renglón es para
                                  COBRAR y nada más. El modal completo sigue en el botón
                                  "Registrar pago" de la cabecera; cancelar un adeudo se hace
                                  desde Finanzas › Adeudos. */}
                              <QuickPay
                                debtId={d.debt_id}
                                remaining={resta}
                                debtType={d.debt_type}
                                month={d.month}
                                concept={etiqueta}
                                onPaid={() => { setPays(null); loadDebt(); load(); }}
                              />
                              {/* CANCELAR el adeudo (Carlos 15-ago): no se borra — se guarda el
                                  motivo para poder revisarlo después.
                                  🚨 17-sep-2026: antes sólo salía en `manual`/`clone`, así que las
                                  MULTAS (source 'auto', 139 en total) y los adeudos importados de
                                  Kydemy no tenían botón y había que cancelarlos por terminal — es
                                  lo que pasó con las 17 multas sabatinas y con el adeudo de Irma
                                  Moreno, congelado desde abril. El veto a los de Kydemy se apoyaba
                                  en que «vuelven en el delta de las 06:30», y eso es falso:
                                  40-debts.js está fuera del delta a propósito. Ahora se cancela
                                  cualquier adeudo; el backend valida igual. */}
                              <button onClick={() => { setCancelando(d.debt_id); setMotivoCancel(''); }}
                                title="Cancelar este adeudo (pide el motivo y queda en el historial)"
                                aria-label="Cancelar este adeudo"
                                className="rounded-lg border border-red-500/40 p-1.5 text-red-600 transition hover:bg-red-500/10 dark:text-red-400">
                                <Ban size={15} />
                              </button>
                            </>
                          )}
                        </div>
                        {cancelando === d.debt_id && (
                          <div className="w-full rounded-lg border border-red-500/30 bg-red-500/[0.06] p-2.5">
                            <label htmlFor={`mot-${d.debt_id}`} className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-content-2">
                              ¿Por qué se cancela?
                            </label>
                            <div className="flex flex-wrap items-center gap-2">
                              <input id={`mot-${d.debt_id}`} autoFocus value={motivoCancel}
                                onChange={(e) => setMotivoCancel(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter' && motivoCancel.trim()) cancelarAdeudo(d.debt_id); if (e.key === 'Escape') setCancelando(null); }}
                                placeholder="Ej.: se dio de baja, error de captura, cortesía…"
                                className={`${inputCls} min-w-[14rem] flex-1`} />
                              <button onClick={() => cancelarAdeudo(d.debt_id)} disabled={!motivoCancel.trim() || saving}
                                className="rounded-lg bg-red-600 px-3 py-2 text-xs font-semibold text-white transition hover:bg-red-700 disabled:opacity-40">
                                Cancelar adeudo
                              </button>
                              <button onClick={() => setCancelando(null)}
                                className="rounded-lg border border-line bg-surface px-3 py-2 text-xs font-medium text-content-2 transition hover:bg-surface-2">
                                Volver
                              </button>
                            </div>
                            <p className="mt-1 text-[11px] text-content-3">No se borra: queda guardado con el motivo, la fecha y quién lo canceló.</p>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
            {loadingTab && !pays ? (
              <div className="space-y-2">{[0, 1, 2, 3].map((i) => <div key={i} className="h-12 animate-pulse rounded-lg bg-skeleton" />)}</div>
            ) : !pays || pays.rows.length === 0 ? (
              <EmptyState icon={CreditCard} title="Sin pagos registrados" subtitle="Aún no hay pagos a nombre de este alumno." />
            ) : (() => {
              // Los adeudos CANCELADOS se intercalan en la MISMA línea de tiempo, por fecha
              // (Carlos 15-ago: «debe aparecer simplemente cronológicamente»). Un bloque
              // aparte obligaba a buscarlo; aquí sale donde ocurrió.
              const items = [
                ...debtsCancel.map((d: any) => ({ tipo: 'cancel', fecha: String(d.cancelled_at || ''), row: d })),
                ...pays.rows.map((p) => ({ tipo: 'pago', fecha: String(p.pay_date || p.datetime || '').slice(0, 10), row: p as any })),
              ].sort((a, b) => (a.fecha === b.fecha ? 0 : a.fecha < b.fecha ? 1 : -1));
              // Solo salen los chips de conceptos que esta alumna tiene; si el filtro elegido ya no
              // aplica (otra ficha, o se borró su único pago) se vuelve a «Todos».
              const grupos = GRUPOS_PAGO
                .map((g) => {
                  const rows = items.filter(g.match);
                  return { ...g, n: rows.length, suma: rows.reduce((a, i) => a + (i.tipo === 'pago' ? Number(i.row.amount || 0) : 0), 0) };
                })
                .filter((g) => g.n > 0);
              const activo = grupos.find((g) => g.key === filtroPago);
              const visibles = activo ? items.filter(activo.match) : items;
              return (
              <>
                {/* El «Total histórico» se quitó (Carlos 15-ago): en la ficha del alumno lo que
                    se consulta es qué pagó y de qué mes, no cuánto ha dejado en la vida. El
                    subtotal sí sale al filtrar: ahí la pregunta es «¿cuánto de vitrina?». */}
                {grupos.length > 1 && (
                  <div className="mb-4 flex flex-wrap items-center gap-1.5" role="group" aria-label="Filtrar por concepto">
                    {[{ key: 'todos', label: 'Todos', n: items.length }, ...grupos].map((g) => {
                      const on = (activo ? activo.key : 'todos') === g.key;
                      return (
                        <button key={g.key} onClick={() => setFiltroPago(g.key)} aria-pressed={on}
                          className={`rounded-full border px-2.5 py-1 text-xs font-semibold transition ${on
                            ? 'border-accent-solid bg-accent-solid text-on-accent'
                            : 'border-line bg-surface text-content-2 hover:bg-surface-2'}`}>
                          {g.label} <span className={on ? 'opacity-80' : 'text-content-3'}>{g.n}</span>
                        </button>
                      );
                    })}
                    {activo && activo.key !== 'cancel' && (
                      <span className="ml-auto text-xs text-content-3">
                        {activo.n} pago{activo.n === 1 ? '' : 's'} · <span className="font-semibold text-content">{money(activo.suma)}</span>
                      </span>
                    )}
                  </div>
                )}
                <ol className="relative ml-2 border-l border-line">
                  {visibles.map((item) => {
                    if (item.tipo === 'cancel') {
                      const d: any = item.row;
                      return (
                        <li key={`c-${d.debt_id}`} className="mb-1 ml-4 py-1">
                          {/* Punto gris: no es dinero que entró, es un cargo que se anuló */}
                          <span className="absolute -left-[5px] mt-1.5 h-2.5 w-2.5 rounded-full bg-content-3 ring-4 ring-surface" />
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
                                {/* Sin etiqueta propia ni fondo: el renglón se lee IGUAL que un
                                    pago (concepto + mes). Que está cancelado lo dicen el importe
                                    tachado y la línea de abajo (Carlos 15-ago). */}
                                {d.debt_type && (
                                  <span className={`rounded px-1.5 py-0.5 text-xs font-semibold ${TYPE_COLOR[d.debt_type] || TYPE_COLOR.other}`}>
                                    {TYPE_LABEL[d.debt_type] || d.debt_type}
                                  </span>
                                )}
                                {d.month && <span className="rounded bg-accent/20 px-1.5 py-0.5 text-xs font-bold uppercase text-accent-ink">{monthYearEs(d.month.slice(0, 7) + '-01')}</span>}
                                {d.comment && <span className="text-xs text-content-2">{d.debt_type === 'product' ? sinPrefijoVitrina(d.comment) : d.comment}</span>}
                              </div>
                              <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-content-3">
                                <Ban size={13} className="shrink-0" />
                                <span>Cancelado el {fmtDay(d.cancelled_at)}</span>
                                {d.cancelled_by_name && <span>· {d.cancelled_by_name}</span>}
                                {d.cancel_reason && <span>· «{d.cancel_reason}»</span>}
                              </div>
                            </div>
                            <span className="shrink-0 text-sm font-semibold text-content-3 line-through">{money(Number(d.amount))}</span>
                          </div>
                        </li>
                      );
                    }
                    const p: any = item.row;
                    return (() => {
                    // Los pagos importados de Kydemy no se editan ni se borran (historial).
                    const propio = isAdmin && p.kydemy_id == null;
                    // 🚨 `months` viene de Kydemy como FECHA COMPLETA ('2026-08-01') en 2,659 de los
                    // 2,708 pagos; sólo 49 traen 'YYYY-MM'. El filtro anterior exigía el formato
                    // corto, así que el chip del mes no se pintaba casi nunca (Carlos 15-ago: «que
                    // salga el mes a un lado»). Se aceptan los dos y se recorta a año-mes.
                    const meses = [...new Set(String(p.months || '').split(',')
                      .map((x) => x.trim().slice(0, 7))
                      .filter((x) => /^\d{4}-\d{2}$/.test(x)))];
                    return (
                      <li key={p.payment_id} className="group mb-1 ml-4 py-1">
                        <span className="absolute -left-[5px] mt-1.5 h-2.5 w-2.5 rounded-full bg-accent ring-4 ring-surface" />
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            {/* Chips de mes y de plan/concepto en el renglón (pedido de Karla). */}
                            <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
                              {/* Concepto con color propio (Carlos 15-ago): «Mensualidad» y
                                  «Inscripción» se distinguen sin leer. */}
                              {/* La vitrina importada de Kydemy (`other` + «AyB») se pinta como la
                                  de la app: mismo chip que el filtro «Vitrina» (29-sep). */}
                              <span className={`rounded px-1.5 py-0.5 text-xs font-semibold ${TYPE_COLOR[esVitrina(p) ? 'product' : p.type] || TYPE_COLOR.other}`}>
                                {TYPE_LABEL[esVitrina(p) ? 'product' : p.type] || p.type}
                              </span>
                              {/* El MES que se está pagando va PEGADO al concepto y con el mismo
                                  peso (Carlos 15-ago): es lo primero que se busca al repasar el
                                  historial — «Mensualidad AGO 2026» se lee de un golpe. */}
                              {meses.map((mm) => (
                                <span key={mm} className="rounded bg-accent/20 px-1.5 py-0.5 text-xs font-bold uppercase text-accent-ink">{monthYearEs(mm + '-01')}</span>
                              ))}
                              {/* Kydemy guarda el concepto asociado entre llaves — `{"K-pop Kids"}`
                                  o `{NULL}`. Se limpia al pintarlo: las llaves no significan nada. */}
                              {limpiaAssoc(p.assoc_name) && (
                                <span className="rounded bg-surface-2 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-content-2">{limpiaAssoc(p.assoc_name)}</span>
                              )}
                              {p.debt_id != null && <span className="rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-emerald-700 dark:text-emerald-400">a cuenta de adeudo</span>}
                              {/* Descuento del COBRO: se ve sin pasar el cursor y también en móvil.
                                  Es el dato que faltaba para entender un mes que entró incompleto. */}
                              {Number(p.amount_discount) > 0 && (
                                <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-700 dark:text-amber-400">
                                  −{money(Number(p.amount_discount))}{p.discount_name ? ` · ${p.discount_name}` : ' de descuento'}
                                </span>
                              )}
                            </div>
                            {/* Cuándo se pagó y cómo, en segundo plano: manda el mes de arriba
                                (Carlos 15-ago). El método va como icono con tooltip. */}
                            <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-content-3">
                              {/* Primero CÓMO pagó y luego cuándo (Carlos 15-ago): el icono ancla
                                  el renglón y la fecha se lee a continuación. */}
                              <MetodoPago metodo={p.method} size={13} />
                              <span>{fmtDay(p.pay_date || p.datetime)}</span>
                              {p.comment && <span>· {p.comment}</span>}
                            </div>
                          </div>
                          <div className="flex shrink-0 items-center gap-2">
                            <span className="text-sm font-semibold text-content">{money(p.amount)}</span>
                            <Link to={`/recibo/${p.payment_id}`} title="Ver / imprimir recibo"
                              className="text-content-3 transition hover:text-brand-blue"><Receipt size={15} /></Link>
                            {propio && (
                              <>
                                <button onClick={() => setPayEdit(p)} title="Editar este pago"
                                  className="text-content-3 transition hover:text-brand-blue"><Pencil size={15} /></button>
                                <button onClick={() => borrarPago(p)} title="Eliminar este pago"
                                  className="text-content-3 transition hover:text-red-500"><Trash2 size={15} /></button>
                              </>
                            )}
                          </div>
                        </div>
                      </li>
                    );
                    })();
                  })}
                </ol>
                {visibles.length === 0 && (
                  <p className="py-6 text-center text-sm text-content-3">Nada de este concepto.</p>
                )}
              </>
              );
            })()}
          </div>
        )}

        {/* ===== ASISTENCIA ===== */}
        {tab === 'asistencia' && (
          <div>
            {loadingTab && !att ? (
              <div className="space-y-2">{[0, 1, 2].map((i) => <div key={i} className="h-12 animate-pulse rounded-lg bg-skeleton" />)}</div>
            ) : !att || att.length === 0 ? (
              <EmptyState icon={CalendarCheck} title="Sin registros de asistencia" subtitle="Se irá llenando cada vez que se pase lista en sus clases." />
            ) : (
              <>
                {/* Los tres contadores (asistencias · faltas · % del mes) se quitaron el 15-ago
                    a petición de Carlos: en la ficha se consulta el detalle, no el resumen. Si
                    hacen falta, su sitio son los reportes. */}

              {/* Toggle de vista: calendario mensual (estilo Kydemy) · lista cronológica */}
              <div className="mb-3 flex justify-end">
                <div className="flex items-center gap-0.5 rounded-lg border border-line bg-surface-2 p-0.5">
                  {(['calendario', 'lista'] as const).map((v) => (
                    <button key={v} type="button" onClick={() => setAttView(v)}
                      className={`rounded-md px-2.5 py-1 text-xs font-medium capitalize transition ${attView === v ? 'bg-accent text-on-accent' : 'text-content-3 hover:text-content'}`}>
                      {v}
                    </button>
                  ))}
                </div>
              </div>

              {attView === 'calendario' ? (
                <AttendanceCalendar rows={att} />
              ) : (
              // Lista AGRUPADA POR MES con el día de la semana delante (Carlos 15-ago: «no es tan
              // clara»). Antes era un hilo continuo de fechas sueltas: para saber si faltó un
              // martes había que leer fecha por fecha.
              <div className="space-y-4">
                {(() => {
                  const porMes = new Map<string, typeof att>();
                  for (const a of att) {
                    const ym = String(a.date || '').slice(0, 7);
                    if (!porMes.has(ym)) porMes.set(ym, [] as any);
                    (porMes.get(ym) as any[]).push(a);
                  }
                  return [...porMes.entries()].map(([ym, filas]) => {
                    const asis = filas.filter((x: any) => ['present', 'attended', 'made_up'].includes(x.status || '')).length;
                    const falt = filas.filter((x: any) => x.status === 'absent').length;
                    return (
                      <section key={ym}>
                        <h4 className="mb-2 flex flex-wrap items-center gap-2 border-b border-line pb-1.5 text-xs font-bold uppercase tracking-wider text-content">
                          <CalendarCheck size={13} className="text-accent-ink" />
                          {ym ? monthYearEs(ym + '-01') : 'Sin fecha'}
                          <span className="ml-auto flex items-center gap-2 font-normal normal-case tracking-normal">
                            {asis > 0 && <span className="text-emerald-600 dark:text-emerald-400">{asis} asistió</span>}
                            {falt > 0 && <span className="text-red-600 dark:text-red-400">{falt} faltó</span>}
                          </span>
                        </h4>
                        <ul className="space-y-1">
                          {filas.map((a: any) => {
                            const lbl = ATT_LABEL[a.status || ''] || { txt: a.status || '—', cls: 'text-content-2' };
                            const d = new Date(`${String(a.date).slice(0, 10)}T12:00:00`);
                            const dia = isNaN(d.getTime()) ? '' : WEEKDAYS_SHORT[d.getDay()];
                            const num = isNaN(d.getTime()) ? '' : d.getDate();
                            return (
                              <li key={a.attendance_id}
                                className="relative flex items-center gap-3 overflow-hidden rounded-xl border border-line bg-canvas py-2 pl-4 pr-3">
                                <span className="absolute inset-y-0 left-0 w-1.5" style={{ background: colorClase(a.color) }} />
                                {/* Día de la semana + número: se ve de un vistazo si falla siempre el mismo día */}
                                <span className="w-11 shrink-0 text-center">
                                  <span className="block text-[10px] font-semibold uppercase text-content-3">{dia}</span>
                                  <span className="block font-display text-base font-bold leading-none text-content">{num}</span>
                                </span>
                                <span className="min-w-0 flex-1 truncate text-sm font-medium text-content">{a.class_name || 'Clase'}</span>
                                <span className={`shrink-0 text-sm font-semibold ${lbl.cls}`}>{lbl.txt}</span>
                              </li>
                            );
                          })}
                        </ul>
                      </section>
                    );
                  });
                })()}
              </div>
              )}
              </>
            )}
          </div>
        )}

        {/* ===== ARCHIVOS ===== */}
        {tab === 'archivos' && <MemberFiles memberId={Number(id)} isAdmin={isAdmin} />}
      </div>
      )}

      {payOpen && (
        <PaymentForm
          prefill={{ account_id: m.account_id, firstname: m.firstname, lastname: m.lastname, has_photo: m.has_photo ? 1 : 0 } as any}
          onClose={() => setPayOpen(false)}
          onCreated={() => { setPayOpen(false); setPays(null); setTab('pagos'); loadDebt(); load(); }}
        />
      )}

      {abono && (
        <PaymentForm
          abono={abono}
          onClose={() => setAbono(null)}
          onCreated={() => { setAbono(null); setPays(null); loadDebt(); load(); }}
        />
      )}

      {/* Corregir / eliminar un pago ya capturado (el propio modal avisa del resultado) */}
      {payEdit && (
        <PaymentForm
          prefill={{ account_id: m.account_id, firstname: m.firstname, lastname: m.lastname, has_photo: m.has_photo ? 1 : 0 } as any}
          edit={{
            payment_id: payEdit.payment_id, type: payEdit.type, method: payEdit.method, amount: payEdit.amount,
            pay_date: payEdit.pay_date || (payEdit.datetime ? String(payEdit.datetime).slice(0, 10) : null),
            months: payEdit.months, assoc_name: payEdit.assoc_name, comment: payEdit.comment,
            currency: payEdit.currency, usd_amount: payEdit.usd_amount, fx_rate: payEdit.fx_rate, mxn_amount: payEdit.mxn_amount,
          }}
          onClose={() => setPayEdit(null)}
          onCreated={() => { setPayEdit(null); setPays(null); loadDebt(); load(); }}
        />
      )}

      {/* Alta de deuda desde la ficha (el alumno viene fijo) */}
      {debtFormOpen && (
        <DebtForm
          prefill={{ account_id: m.account_id, firstname: m.firstname, lastname: m.lastname, has_photo: m.has_photo ? 1 : 0 } as any}
          onClose={() => setDebtFormOpen(false)}
          onCreated={() => { setDebtFormOpen(false); loadDebt(); load(); }}
        />
      )}

      {accessOpen && <AccessModal accountId={m.account_id} onClose={() => setAccessOpen(false)} />}
    </div>
  );
}
